import React from 'react';
import Icon from '@/components/ui/AppIcon';

interface StarterOption {
  id: string;
  label: string;
  subLabel: string;
  icon: string;
  aiScript: string;
}

interface EmptyStateProps {
  // Now accepts both the user text AND the hidden AI script
  onStarterClick: (userText: string, aiScript: string) => void;
}

const STARTERS: StarterOption[] = [
  {
    id: 'patterns',
    label: "How can I break out of my thinking patterns?",
    subLabel: "Awareness",
    icon: "HeartIcon",
    aiScript: "Thoughts often run on tracks like old trains—looping the same route, day after day. To derail the train, we must first hear the wheels screeching.\n\nDon't tell me the whole story yet. Just tell me: **What is the one specific thought that has looped in your mind the most today?**"
  },
  {
    id: 'insight',
    label: "Help me see what I'm not seeing...",
    subLabel: "Insight",
    icon: "LightbulbIcon",
    aiScript: "We often miss the truth because we are standing too close to the painting. We see only the brushstrokes of our own emotions, not the full picture.\n\nDescribe your current trouble to me, but **pretend you are a stranger watching it happen to someone else.** What does the stranger see?"
  },
  {
    id: 'perception',
    label: "What's the difference between reality and my story about it?",
    subLabel: "Perception",
    icon: "SparklesIcon",
    aiScript: "Reality is the cold wind. The 'Story' is: 'I hate the cold, it shouldn't be winter, this is unfair.' The wind is innocent; the Story is what hurts.\n\nName one thing bothering you right now. Then, **strip away the adjectives** (bad, hard, unfair). What is the raw fact left behind?"
  },
  {
    id: 'suffering',
    label: "How do I stop creating my own suffering?",
    subLabel: "Self-Inquiry",
    icon: "FaceSmileIcon",
    aiScript: "Suffering is not something that happens to you. It is often something you do—like holding onto a burning coal and waiting for it to get cold.\n\nLook at your hands right now. Metaphorically speaking, **what are you holding onto that is burning you?**"
  }
];

const EmptyState = ({ onStarterClick }: EmptyStateProps) => {
  return (
    <div className="flex flex-col items-center justify-center h-full max-w-2xl mx-auto px-6 animate-fade-in">
      
      <div className="text-center mb-10 space-y-4">
        <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
          <Icon name="SparklesIcon" size={32} className="text-primary" />
        </div>
        <h2 className="text-2xl font-serif text-foreground">
          Stillpoint
        </h2>
        <p className="text-muted-foreground max-w-md mx-auto">
          Clarify on demand.
        </p>
      </div>

      <div className="w-full space-y-4">
        <p className="text-sm font-medium text-muted-foreground ml-1">Try starting with:</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {STARTERS.map((starter) => (
            <button
              key={starter.id}
              onClick={() => onStarterClick(starter.label, starter.aiScript)}
              className="flex flex-col text-left p-4 rounded-xl border border-border bg-card hover:bg-accent/5 hover:border-accent/20 transition-all duration-200 group shadow-sm hover:shadow-md"
            >
              <div className="flex items-start justify-between w-full mb-2">
                <Icon 
                  name={starter.icon as any} 
                  size={20} 
                  className="text-muted-foreground group-hover:text-primary transition-colors" 
                />
                <span className="text-xs font-medium text-muted-foreground/60 group-hover:text-primary/60 uppercase tracking-wider">
                  {starter.subLabel}
                </span>
              </div>
              <span className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">
                {starter.label}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default EmptyState;